"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Who sees it",
    rule: "Settings → Inventory → Inventory config. Title Stock Source Configuration. Needs Inventory Config view. Save Changes needs edit permission",
  },
  {
    topic: "Stock Check Source",
    rule: "Quotation, Sales Order, Delivery Challan (only if Delivery Challan is on the company), Invoice. Required. Changing it clears Stock Deduct Source",
  },
  {
    topic: "Stock Deduct Source",
    rule: "Disabled until a check source is chosen. Options: Delivery Challan (if on) and Invoice — filtered so deduct is not earlier in the sales flow than check. Help: Source for deducting stock on fulfillment",
  },
  {
    topic: "Stock Add Source",
    rule: "GRPO (only if GRPO is on the company) or Bill. Help: Source for adding stock",
  },
];

const sections = [
  {
    heading: "What this is for",
    subsections: [{ heading: "1. Three source pickers" }],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

const Steps = ({ items }) => (
  <ol className="space-y-3 mb-4">
    {items.map((text, i) => (
      <li key={i} className="flex items-start gap-3 list-none">
        <span className="w-6 h-6 rounded-full bg-[#111827] text-white text-[12px] geist-medium flex items-center justify-center flex-shrink-0 mt-0.5">
          {i + 1}
        </span>
        <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
          {text}
        </p>
      </li>
    ))}
  </ol>
);

const Flow = ({ steps }) => (
  <p className="text-[15px] geist-regular text-[#374151] leading-relaxed mb-4 px-4 py-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-[10px]">
    {steps.map((step, i) => (
      <span key={`${step}-${i}`}>
        {i > 0 && (
          <span className="text-[#9CA3AF] geist-medium"> {"→"} </span>
        )}
        <span className="geist-medium text-[#111827]">{step}</span>
      </span>
    ))}
  </p>
);

export default function InventoryConfigPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Inventory Config
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Users with Inventory Config access — when sales checks /
            deducts stock, and when purchase adds it
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            Hub label <B>Inventory config</B>. The screen is{" "}
            <B>Stock Source Configuration</B>. It is these three pickers, not
            warehouses or item masters.
          </p>
          <Flow
            steps={[
              "Settings",
              "Inventory",
              "Inventory config",
              "pick sources",
              "Save Changes",
            ]}
          />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this is for
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Three source pickers
          </h3>
          <Definitions
            items={[
              {
                term: "Stock Check Source",
                desc: "Primary source for checking stock availability — which sales document must have enough qty before you proceed",
              },
              {
                term: "Stock Deduct Source",
                desc: "Which sales document actually reduces warehouse qty on fulfillment",
              },
              {
                term: "Stock Add Source",
                desc: "Which purchase document increases warehouse qty",
              },
              {
                term: "What it affects",
                desc: "Stock availability checks and qty movements on the chosen sales / purchase documents. Wrong pairing (check on Invoice but deduct on an earlier document) is blocked by the deduct picker",
              },
            ]}
          />
          <Steps
            items={[
              <>
                Set all three required pickers.
              </>,
              <>
                Click <B>Save Changes</B>.
              </>,
            ]}
          />
          <Callout type="note">
            Delivery Challan and GRPO only appear if those features are on
            the company.
          </Callout>
          <Screenshot caption="Stock Source Configuration — Check, Deduct, Add, Save Changes" />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Topic
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((row) => (
                  <tr
                    key={row.topic}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.topic}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {row.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/inventory/view-inventory"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              View Inventory <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              On-hand qty that these sources add and deduct
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/invoices"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Invoices <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Default check and deduct source when DC is not in the mix
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
